import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { AdminFetchGigsafeConfig, AdminSaveGigsafeConfig } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { GigsafeConfig, SaveGigsafeConfigPayload } from 'src/interfaces/gigsafeConfig'
import { getGigsafeConfig } from 'src/selectors/gigsafeConfig'
import { parseGigsafeConfigResponse } from 'src/utils/gigsafeConfig'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'

function resolveErrorMessage(error: unknown, fallback: string): string {
  const errorDetails = error as { payload?: { message?: string }; message?: string }

  if (typeof errorDetails?.payload?.message === 'string') {
    return errorDetails.payload.message
  }

  if (typeof errorDetails?.message === 'string') {
    return errorDetails.message
  }

  return fallback
}

interface FetchGigsafeConfigPayload {
  partnerId?: string
}

/**
 * The save endpoints (POST/PUT) are expected to echo back the persisted config, which becomes
 * `state.config` and drives both the next save's POST-vs-PUT decision and the form's
 * `toFormValues`. `parseGigsafeConfigResponse` only rejects `null`/array bodies; it does not
 * confirm the object is actually a config row. Require a string `id` here so a malformed or
 * unexpected save response fails loudly through the saga's existing `catch` instead of quietly
 * becoming the new (wrong) `state.config`.
 */
function assertSavedGigsafeConfig(payload: unknown): GigsafeConfig {
  const parsed = parseGigsafeConfigResponse(payload)

  if (parsed === null || typeof (parsed as { id?: unknown }).id !== 'string') {
    throw new Error('Gigsafe config save response is missing an id')
  }

  return parsed
}

export function* onFetchGigsafeConfig(action: AsyncAction<FetchGigsafeConfigPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchGigsafeConfig.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // `returnResponse: true` reads the body as text instead of `response.json()`. A partner
    // with no config row gets a 200 with an empty body, which `.json()` would reject with a
    // SyntaxError — see parseGigsafeConfigResponse.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchGigsafeConfig,
      {
        method: 'GET',
        query: { partnerId },
        returnResponse: true,
      },
    )

    const config = parseGigsafeConfigResponse(payload)

    yield put(AdminFetchGigsafeConfig.success({ partnerId, config }))

    if (action.next) {
      action.next(null, config)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeConfig.onFetchGigsafeConfig] error' })

    yield put(AdminFetchGigsafeConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load customer settings'))
    }
  }
}

// Under this repo's `es5` + `strict` tsconfig, TS infers a generator's resume ("next") type by
// combining the expected type at *every* `yield` in the function body. This handler mixes a
// `yield select(...)` (resumed with `GigsafeConfig | null`) with three `yield call(...)` sites
// (each resumed with `{ payload: GigsafeConfig }`), and TS collapses those into an
// unsatisfiable intersection rather than a union — no single value (including a cast at the
// `select` site alone) can satisfy it. The explicit `any` resume type below sidesteps that
// inference; the three `call(...)` sites keep their own precise `{ payload: GigsafeConfig }`
// annotations, matching `onFetchGigsafeConfig` and `customer.ts`. Do not copy this pattern onto
// a handler that doesn't mix `select` with multiple differently-shaped `call` resumes.
export function* onSaveGigsafeConfig(
  action: AsyncAction<SaveGigsafeConfigPayload>,
): Generator<unknown, void, any> {
  const body = action.payload
  const { partnerId } = body

  try {
    // POST 409s when a config already exists and PUT 404s when it does not, so the verb is
    // decided from loaded server state rather than by the component.
    const existing: GigsafeConfig | null = yield select(getGigsafeConfig)
    let saved: GigsafeConfig

    if (existing === null) {
      try {
        const { payload }: { payload: unknown } = yield call(
          callSecureApi,
          Endpoints.AdminCreateGigsafeConfig,
          {
            method: 'POST',
            body,
          },
        )

        saved = assertSavedGigsafeConfig(payload)
      } catch (createError: unknown) {
        // Another admin created the row between our fetch and this save. Retrying as an
        // update is what the admin actually asked for; surfacing "already exists" is not.
        if ((createError as { status?: number })?.status !== 409) {
          throw createError
        }

        const { payload }: { payload: unknown } = yield call(
          callSecureApi,
          Endpoints.AdminUpdateGigsafeConfig,
          {
            method: 'PUT',
            body,
          },
        )

        saved = assertSavedGigsafeConfig(payload)
      }
    } else {
      const { payload }: { payload: unknown } = yield call(
        callSecureApi,
        Endpoints.AdminUpdateGigsafeConfig,
        {
          method: 'PUT',
          body,
        },
      )

      saved = assertSavedGigsafeConfig(payload)
    }

    yield put(AdminSaveGigsafeConfig.success({ partnerId, config: saved }))

    if (action.next) {
      action.next(null, saved)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeConfig.onSaveGigsafeConfig] error' })

    yield put(AdminSaveGigsafeConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to save customer settings'))
    }
  }
}

export function* gigsafeConfigSaga() {
  // Tab and customer changes re-trigger the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchGigsafeConfig.type.REQUEST, onFetchGigsafeConfig)
  // Every save must run.
  yield takeEvery(AdminSaveGigsafeConfig.type.REQUEST, onSaveGigsafeConfig)
}
